import { isEmpty } from '@jfs/utils'
import { flow, Instance, types } from 'mobx-state-tree'
import { addMerchantConfig } from '../../network/addMerchantConfig'
import { addMerchantConfigList } from '../../network/addMerchantConfigList'
import { addMerchantMapping } from '../../network/addMerchantMapping'
import { bulkMerchantProcessApprovals } from '../../network/bulkMerchantProcessApprovals'
import { createCustomer } from '../../network/createCustomer'
import { createVpa } from '../../network/createVpa'
import { deleteMerchantMapping } from '../../network/deleteMerchantMapping'
import { extendAppSessionAPI } from '../../network/ExtendAppSession'
import { generateQr } from '../../network/generateQr'
import { getAllMerchants } from '../../network/getAllMerchants'
import { getMerchantById } from '../../network/getMerchantById'
import { grantPermissions } from '../../network/grantPermissions'
import { insertCso } from '../../network/insertCso'
import { merchantProcessApprovals } from '../../network/merchantProcessApprovals'
import { registerUser } from '../../network/registerUser'
import { sapLogin } from '../../network/sapLogin'
import { searchMerchant } from '../../network/searchMerchant'
import { trustedUserAuth } from '../../network/trustedUserAuth'
import { updateMerchantMapping } from '../../network/updateMerchantMapping'
import { downloadQrFile, generateVpaOrQid, getErrorMessage, MessageTypes } from '../../utils'
import { getIdNameValue, InstrumentTypes, stateGstMap, TransactionType } from '../../utils/constants'
import { RootStore } from '../store'
import { insertMerchant, MerchantData } from './../../network/insertMerchant'
import { Bank } from './Bank'
import { CardItem } from './Card'
import { CardlessEMI } from './CardlessEMI'
import { EMI } from './EMI'
import { FoodCard } from './FoodCard'
import { PayLater } from './PayLater'
import { Wallet } from './Wallet'

export const AllowedPaymentModes = types.model('AllowedPaymentModes', {
  rank: types.optional(types.string, ''),
  methodType: types.optional(types.string, ''),
  methodSubType: types.optional(types.string, ''),
  cardType: types.array(types.optional(types.number, 0)),
  bankId: types.array(types.optional(types.number, 0)),
})

const CardEMI = types.model('CardEMI', {
  emiList: types.array(EMI),
})

export const Preferences = types.model('Preferences', {
  templateId: types.optional(types.number, 0),
  defaultpaymode: types.optional(types.string, ''),
  includepaymodes: types.optional(types.string, ''),
  excludepaymodes: types.optional(types.string, ''),
  finalpaymodes: types.optional(types.string, ''),
  optedpaymodes: types.optional(types.string, ''),
  retrycount: types.optional(types.string, ''),
  cardTrustedMerchants: types.optional(types.string, ''),
  enableForCommonSavedCards: types.optional(types.string, ''),
  ssConFlag: types.optional(types.string, ''),
  uitype: types.optional(types.string, ''),
  isClickable: types.optional(types.string, ''),
  paymenttype: types.optional(types.string, ''),
  bankList: types.array(Bank),
  foodCardList: types.array(FoodCard),
  walletList: types.array(Wallet),
  cardLessEMIList: types.array(CardlessEMI),
  EMILList: types.array(EMI),
  cardEMIList: types.map(CardEMI),
  payLaterList: types.array(PayLater),
  expMon: types.optional(types.string, ''),
  expYr: types.optional(types.string, ''),
  cardList: types.optional(types.array(CardItem), []),
  selectedCardType: types.optional(types.string, 'Debit Card'),
  selectedCardTypeId: types.optional(types.string, '110'),
  corpBankList: types.array(Bank),
})

const Profile = types.model('Profile', {
  annualRevenueSegment: types.optional(types.string, ''),
  brandName: types.optional(types.string, ''),
  businessCategory: types.optional(types.string, ''),
  businessLegalName: types.optional(types.string, ''),
  deliveryMode: types.optional(types.string, ''),
  level: types.optional(types.string, ''),
  mcc: types.optional(types.string, ''),
  merchantEmailID: types.optional(types.string, ''),
  merchantMobileNumber: types.optional(types.string, ''),
  merchantRisk: types.optional(types.string, ''),
  merchantStatus: types.optional(types.string, ''),
  merchantType: types.optional(types.string, ''),
  yearsOfBusiness: types.optional(types.string, ''),
})

const Terminal = types.model('Terminal', {
  acceptPayment: types.optional(types.string, ''),
  businessVPA: types.optional(types.string, ''),
  isDefault: types.optional(types.boolean, false),
  mid: types.optional(types.string, ''),
  status: types.optional(types.string, ''),
  terminalID: types.optional(types.string, ''),
  qrUrl: types.optional(types.string, ''),
})

const Merchant = types.model('Merchant', {
  type: types.optional(types.literal('merchant'), 'merchant'),
  id: types.optional(types.string, ''),
  merchantId: types.optional(types.string, ''),
  entityId: types.maybeNull(types.number),
  txnType: types.maybeNull(types.number),
  instrumentType: types.maybeNull(types.number),
  reqType: types.optional(types.string, ''),
  meCode: types.optional(types.string, ''), // PG Merchant ID
  pgSecurityId: types.optional(types.string, ''),
  emiCode: types.optional(types.string, ''),
  offerCode: types.optional(types.string, ''),
  productId: types.optional(types.string, ''),
  mappingStatus: types.optional(types.string, ''),
  secret: types.optional(types.string, ''),
  chiper: types.optional(types.string, ''),
  mcfg1: types.optional(types.string, ''),
  mcfg2: types.optional(types.string, ''),
  allowedPaymentModes: types.array(types.optional(AllowedPaymentModes, {})),
  notAllowedPaymentModes: types.array(types.optional(AllowedPaymentModes, {})),
  preferences: types.optional(Preferences, {}), // part of  merchant
  profile: types.optional(Profile, {}),
  terminals: types.array(types.optional(Terminal, {})),
})

export interface BulkOnnboardResponse {
  status: string
  message: string
  code: string
  name?: string
  businessLegalName?: string
  UPIQRUrl?: string
  mobile?: string
  address?: string
  pincode?: string
  vpa?: string
}

const MerchantMapping = types
  .model('MerchantMapping', {
    merchant: types.optional(Merchant, {}),
    merchants: types.array(Merchant),
  })
  .actions((self) => ({
    bulkGenericAPI: flow(function* (func: any, merchantData: MerchantData[], delay: number) {
      const errorData: BulkOnnboardResponse[] = []
      for (let i = 0; i < merchantData.length; i++) {
        try {
          if (merchantData[i].mid !== 'NP') {
            const response = yield func(merchantData[i], RootStore.session.getTokens)
            errorData.push({ status: 'SUCCESS', message: JSON.stringify(response), code: '' })
          } else {
            errorData.push({ status: 'FAILURE', message: 'Not Allowed', code: '' })
          }
        } catch (e) {
          errorData.push({ status: 'Failure', message: getErrorMessage(e), code: (e as any)?.data?.error?.code ?? '' })
        }
        yield new Promise((r) => setTimeout(r, delay))
      }
      return errorData
    }),
    singleGenerateQR: flow(function* (merchantData: MerchantData) {
      let errorCode = ''
      let resp
      try {
        resp = yield generateQr(merchantData, RootStore.session.getTokens)
        // Below is to check the errorcode in the response as its arraybuffer-
        try {
          const errorFileResponse =
            resp?.data?.status !== 'SUCCESS' && JSON.parse(Buffer.from(resp?.data).toString('utf8'))
          errorCode = errorFileResponse?.error?.code ?? ''
          if (errorCode === '33607' || errorCode === '33306') {
            if (errorCode === '33607') {
              try {
                yield extendAppSessionAPI()
              } catch (e) {
                throw { throwable: true, error: e }
              }
              return yield (self as any).singleGenerateQR(merchantData)
            } else {
              try {
                yield extendAppSessionAPI()
              } catch (e) {
                throw { throwable: true, error: e }
              }
              try {
                return yield (self as any).singleGenerateQR(merchantData)
              } catch (e) {
                throw { throwable: true, error: e }
              }
            }
          }
        } catch (e) {
          if ((e as any).throwable) {
            throw (e as any).error
          }
        }
        if (window && errorCode === '' && resp && resp?.data) {
          downloadQrFile(resp, merchantData.mid)
        }
      } catch (error) {
        throw error
      }
      return 'Download Successful'
    }),
  }))
  .actions((self) => ({
    getById: flow(function* getById(id: number) {
      try {
        const response = yield getMerchantById(id, RootStore.session.getTokens)
        const own = self
        own.merchant = response
        return response
      } catch (error) {
        RootStore.message.setMessage(getErrorMessage(error), MessageTypes.ERROR)
        throw error
      }
    }),

    getAll: flow(function* getAll(data: any, paginationInfo: any) {
      try {
        let response = yield getAllMerchants(data, paginationInfo, RootStore.session.getTokens)
        return response
      } catch (error) {
        RootStore.message.setMessage(getErrorMessage(error), MessageTypes.ERROR)
        throw error
      }
    }),

    addMP: flow(function* addMP(data: any) {
      try {
        const response = yield addMerchantMapping(data, RootStore.session.getTokens)
        RootStore.message.setMessage(response?.message, MessageTypes.SUCCESS)
        return response
      } catch (error) {
        RootStore.message.setMessage(getErrorMessage(error), MessageTypes.ERROR)
        throw error
      }
    }),

    processApprovals: flow(function* processApprovals(data: any) {
      try {
        const response = yield merchantProcessApprovals(data, RootStore.session.getTokens)
        RootStore.message.setMessage(response?.message, MessageTypes.SUCCESS)
        return response
      } catch (error) {
        RootStore.message.setMessage(getErrorMessage(error), MessageTypes.ERROR)
        throw error
      }
    }),

    updateMP: flow(function* updateMP(data: any) {
      try {
        const response = yield updateMerchantMapping(data, RootStore.session.getTokens)
        RootStore.message.setMessage(response?.message, MessageTypes.SUCCESS)
        return response
      } catch (error) {
        RootStore.message.setMessage(getErrorMessage(error), MessageTypes.ERROR)
        throw error
      }
    }),
    deleteMP: flow(function* deleteMP(data: any) {
      try {
        const response = yield deleteMerchantMapping(data, RootStore.session.getTokens)
        RootStore.message.setMessage(response?.message, MessageTypes.SUCCESS)
        return response
      } catch (error) {
        RootStore.message.setMessage(getErrorMessage(error), MessageTypes.ERROR)
        throw error
      }
    }),
    bulkAPIGOnboarding: flow(function* (merchantData: MerchantData[], delay: number) {
      const errorData: BulkOnnboardResponse[] = []
      for (let i = 0; i < merchantData.length; i++) {
        const mid = merchantData[i].mid
        try {
          if (mid !== 'NP') {
            const userData = yield searchMerchant(mid, RootStore.session.getTokens)
            const authorizationCode = yield trustedUserAuth(userData, RootStore.session.getTokens)
            const jfsUserid = yield registerUser(userData, authorizationCode, RootStore.session.getTokens)
            delete userData.mobile
            errorData.push({ status: 'SUCCESS', message: JSON.stringify({ ...userData, jfsUserid }), code: '' })
          } else {
            errorData.push({ status: 'FAILURE', message: 'Register User is not allowed', code: '' })
          }
        } catch (e) {
          errorData.push({ status: 'Failure', message: getErrorMessage(e), code: (e as any)?.data?.error?.code ?? '' })
        }
        yield new Promise((r) => setTimeout(r, delay))
      }
      return errorData
    }),
    bulkGrantPermission: flow(function* (merchantData: MerchantData[], delay: number) {
      const errorData: BulkOnnboardResponse[] = []
      for (let i = 0; i < merchantData.length; i++) {
        const mid = merchantData[i].mid
        try {
          if (mid !== 'NP') {
            const userData = yield searchMerchant(mid, RootStore.session.getTokens)
            const response = yield grantPermissions({ ...userData, ...merchantData[i] }, RootStore.session.getTokens)
            errorData.push({ status: 'SUCCESS', message: JSON.stringify(response), code: '' })
          } else {
            errorData.push({ status: 'FAILURE', message: 'Grant permission is not allowed', code: '' })
          }
        } catch (e) {
          errorData.push({ status: 'Failure', message: getErrorMessage(e), code: (e as any)?.data?.error?.code ?? '' })
        }
        yield new Promise((r) => setTimeout(r, delay))
      }
      return errorData
    }),
    bulkInsertMerchant: flow(function* (merchantData: MerchantData[], delay) {
      return yield self.bulkGenericAPI(insertMerchant, merchantData, delay)
    }),
    bulkAddConfig: flow(function* (merchantData: MerchantData[], delay) {
      return yield self.bulkGenericAPI(addMerchantConfig, merchantData, delay)
    }),
    bulkAddConfigList: flow(function* (merchantData: MerchantData[], delay) {
      return yield self.bulkGenericAPI(addMerchantConfigList, merchantData, delay)
    }),
    bulkCreateVpa: flow(function* (merchantData: MerchantData[], delay) {
      const errorData: BulkOnnboardResponse[] = []
      for (let i = 0; i < merchantData.length; i++) {
        try {
          let userData
          if (merchantData[i].mid !== 'NP') {
            userData = yield searchMerchant(merchantData[i].mid, RootStore.session.getTokens, true)
          }
          let vpa = merchantData[i].vpa
          if (!vpa) {
            vpa = generateVpaOrQid(true, merchantData[i].program_identifier ?? '', merchantData[i].vpa_prefix ?? '')
          }
          const modifiedMerchantData =
            merchantData[i].mid !== 'NP'
              ? {
                  mid: merchantData[i].mid,
                  vpa,
                  mobile: userData.mobile,
                  terminalId: merchantData[i]?.terminalId ? merchantData[i]?.terminalId : userData.terminalId,
                }
              : { mid: merchantData[i].mid, vpa }
          let response = yield createVpa(modifiedMerchantData, RootStore.session.getTokens)
          errorData.push({ vpa, status: 'SUCCESS', message: JSON.stringify(response), code: '' })
        } catch (e) {
          errorData.push({
            vpa: (e as any)?.vpa ?? merchantData[i]?.vpa,
            status: 'Failure',
            message: getErrorMessage(e),
            code: (e as any)?.data?.error?.code ?? '',
          })
        }
        yield new Promise((r) => setTimeout(r, delay))
      }
      return errorData
    }),
    bulkGenerateQR: flow(function* (merchantData: MerchantData[], delay) {
      const errorData: BulkOnnboardResponse[] = []
      for (let i = 0; i < merchantData.length; i++) {
        try {
          let userData
          let intentId
          let modifiedMerchantData = merchantData[i]
          if (merchantData[i].mid !== 'NP') {
            userData = yield searchMerchant(merchantData[i].mid, RootStore.session.getTokens, false, true)
          }
          if (modifiedMerchantData.tr_identifier) {
            intentId = modifiedMerchantData.tr_identifier
            if (modifiedMerchantData.sequence_flag === 'Y') {
              intentId = generateVpaOrQid(false, intentId)
            }
            modifiedMerchantData.intentId = intentId
          }
          modifiedMerchantData = userData ? { ...merchantData[i], mobile: userData.mobile } : modifiedMerchantData
          const resp = yield generateQr(modifiedMerchantData, RootStore.session.getTokens)
          errorData.push({
            status: 'SUCCESS',
            message: '',
            code: '',
            name: userData?.name ?? '',
            mobile: userData?.mobile ?? '',
            UPIQRUrl: resp,
            businessLegalName: userData?.businessLegalName ?? '',
            address: userData?.address ?? '',
            pincode: userData?.pincode ?? '',
          })
        } catch (e) {
          errorData.push({ status: 'FAILURE', message: getErrorMessage(e), code: (e as any)?.data?.error?.code ?? '' })
        }
        yield new Promise((r) => setTimeout(r, delay))
      }
      return errorData
    }),
    bulkInsertCso: flow(function* (merchantData: any, delay) {
      const errorData = []
      const failedCsos: any = []
      for (let i = 0; i < merchantData.length; i++) {
        try {
          let userData = merchantData[i]
          if (!userData.terminalId && !!userData.mid) {
            userData = yield searchMerchant(merchantData[i]?.mid, RootStore.session.getTokens, true)
          }
          const modifiedData = { ...userData, mobile: merchantData[i]['CSO Mobile'] }
          const authorizationCode = yield trustedUserAuth(modifiedData, RootStore.session.getTokens)
          const isOnboardFailing = failedCsos.indexOf(merchantData[i]['CSO Mobile'])
          if (isOnboardFailing === -1) {
            yield registerUser(modifiedData, authorizationCode, RootStore.session.getTokens)
          }
          const response = yield insertCso({ ...merchantData[i], ...userData }, RootStore.session.getTokens)
          errorData.push({ status: 'SUCCESS', message: JSON.stringify(response), code: '' })
        } catch (e) {
          errorData.push({ status: 'Failure', message: getErrorMessage(e), code: (e as any)?.data?.error?.code ?? '' })
        }
        yield new Promise((r) => setTimeout(r, delay))
      }
      return errorData
    }),
    sapLoginApi: flow(function* sapLoginApi() {
      try {
        const response = yield sapLogin(RootStore.session.getTokens)
        return response
      } catch (error) {
        RootStore.message.setMessage(getErrorMessage(error), MessageTypes.ERROR)
        throw error
      }
    }),
    sapUpload: flow(function* (sapTokens: any, midData: any, filename: string) {
      const outputs: any = []
      let i = 0
      if (!isEmpty(sapTokens?.csrfToken) && !isEmpty(sapTokens?.session)) {
        for (i = 0; i < midData.length; i++) {
          try {
            if (!midData[i].StateCode || !stateGstMap[midData[i].StateCode as keyof typeof stateGstMap]) {
              outputs.push({
                message: 'Invalid state code',
                status: 'FAILURE',
                response: 'Invalid state code',
              })
              continue
            }
            if (
              !!midData[i]?.GSTNumber &&
              !midData[i]?.GSTNumber?.startsWith(stateGstMap[midData[i].StateCode as keyof typeof stateGstMap])
            ) {
              outputs.push({
                message: 'GST state code mismatch',
                status: 'FAILURE',
                response: 'GST state code mismatch',
              })
              continue
            }
            const resp = yield createCustomer(sapTokens, midData[i], i, filename, RootStore.session.getTokens)
            const statusMsg = resp?.data?.ET_RETURN?.[0]?.MESSAGE ?? ''
            const regex = /Customer\s*Code\s*[0-9]*\s*Created\s*\/\s*Updated\s*for\s*MASCUST/
            outputs.push({
              message: statusMsg,
              status: regex.test(statusMsg) ? 'SUCCESS' : 'FAILURE',
              response: JSON.stringify(resp?.data),
            })
          } catch (e) {
            outputs.push({ message: e?.message ?? '', status: 'Failure', response: JSON.stringify(e?.data) })
          }
        }
      }
      return outputs
    }),
    bulkAddMapping: flow(function* (merchantData: any) {
      const errorData: any = []
      for (let i = 0; i < merchantData.length; i++) {
        const data = merchantData[i]
        try {
          let response
          if (!isEmpty(data.id)) {
            response = yield updateMerchantMapping(data, RootStore.session.getTokens)
          } else {
            response = yield addMerchantMapping(data, RootStore.session.getTokens)
          }

          errorData.push({ status: 'SUCCESS', message: response?.message, code: '' })
        } catch (e) {
          errorData.push({ status: 'Failure', message: getErrorMessage(e), code: (e as any)?.data?.error?.code ?? '' })
        }
        yield new Promise((r) => setTimeout(r, 200))
      }
      return errorData
    }),
    bulkApproval: flow(function* (merchantData: any) {
      const errorData: any = []
      for (let i = 0; i < merchantData.length; i++) {
        const data = merchantData[i]
        try {
          const response = yield bulkMerchantProcessApprovals(data, RootStore.session.getTokens)
          errorData.push({ status: 'SUCCESS', message: response?.message, code: '' })
        } catch (e) {
          errorData.push({ status: 'Failure', message: getErrorMessage(e), code: (e as any)?.data?.error?.code ?? '' })
        }
        yield new Promise((r) => setTimeout(r, 200))
      }
      return errorData
    }),
  }))
  .views((self) => ({
    getMerchantsList(listData: any, keys?: any, searchText?: any) {
      const list = listData ?? self?.merchants
      const data = list?.map((item) => {
        return {
          ID: '' + item?.id,
          merchantId: item?.merchantId,
          entityID: '' + item?.entityId,
          txnType: '' + item?.txnType,
          instrumentType: '' + item?.instrumentType,
          meCode: item?.meCode,
          pgSecurityId: item?.pgSecurityId,
          emiCode: item?.emiCode,
          offerCode: item?.emiCode,
          productId: item?.productId,
          mappingStatus: item?.mappingStatus,
          chiper: item.chiper,
          mcfg1: item.mcfg1,
          mcfg2: item.mcfg2,
          reqType: item?.reqType,
          secret: item?.secret,
          id: '' + item?.id,
          entityId: '' + item?.entityId,
        }
      })
      if (keys?.length > 0) {
        if (data.length > 0) {
          if (keys && keys?.length > 0) {
            const retArr: any = []
            data.map((item: any) => {
              let isMatching = true
              keys.map((obj: any) => {
                const val = isNaN(item[obj?.key]) ? item[obj?.key].toLowerCase() : item[obj?.key]?.toString()
                isMatching = isMatching && val.includes(obj?.searchString.toLowerCase())
              })
              isMatching && retArr.push(item)
              return retArr
            })
            return retArr
          } else {
            return list?.filter((obj: any) =>
              Object.keys(obj).some((key) => obj[key].toLowerCase().includes(searchText.toLowerCase())),
            )
          }
        }
      } else {
        return data
      }
    },
    getMerchant(isEdit: boolean) {
      const item = self.merchant
      const data = {
        merchantId: item?.merchantId ?? '',
        entityId: '' + item?.entityId ?? '',
        txnType: getIdNameValue('' + item?.txnType, TransactionType) ?? '',
        instrumentType: getIdNameValue('' + item?.instrumentType, InstrumentTypes) ?? '',
        meCode: item?.meCode ?? '',
        reqType: item?.reqType ?? '',
        pgSecurityId: item?.pgSecurityId ?? '',
        secret: item?.secret ?? '',
        chiper: item?.chiper ?? '',
        mcfg1: item?.mcfg1 ?? '',
        mcfg2: item?.mcfg2 ?? '',
        emiCode: item?.emiCode ?? '',
        mappingStatus: isEdit ? item?.mappingStatus?.slice(0, -1) ?? '' : item?.mappingStatus ?? '',
      }
      return data
    },
  }))

const merchantdata = MerchantMapping.create({
  merchant: {},
  merchants: [],
})

export type MerchantType = Instance<typeof Merchant>
export { Merchant, merchantdata, MerchantMapping }
